import React from 'react';
import { Box, Chip, Grid, Stack, Typography } from '@mui/material';
import type { MarketTechnicals } from '../../models/MarketValuationTypes';
import { formatCurrency } from '../../utils/CurrencyFormat';

interface Props {
  technicals: MarketTechnicals;
  lastClosePrice?: number | null;
}

function signalColor(label?: string | null): 'success' | 'error' | 'warning' | 'default' {
  if (!label) return 'default';
  const lower = label.toLowerCase();
  if (lower.includes('buy') || lower.includes('bullish') || lower.includes('above')) return 'success';
  if (lower.includes('sell') || lower.includes('bearish') || lower.includes('below') || lower.includes('overbought')) {
    return lower.includes('overbought') || lower.includes('oversold') ? 'warning' : 'error';
  }
  if (lower.includes('oversold')) return 'success';
  return 'default';
}

function MetricCard({
  label,
  value,
  hint,
  chip,
}: {
  label: string;
  value: string;
  hint?: string;
  chip?: { label: string; color: 'success' | 'error' | 'warning' | 'default' };
}) {
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 1,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        height: '100%',
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
        <Typography variant="body2" fontWeight={700}>
          {value}
        </Typography>
        {chip && <Chip size="small" label={chip.label} color={chip.color} variant="outlined" />}
      </Stack>
      {hint && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

const MarketTechnicalsSection: React.FC<Props> = ({ technicals, lastClosePrice }) => {
  const hasContent =
    technicals.rsi != null ||
    technicals.recommendAllLabel ||
    technicals.adx != null ||
    technicals.sma20 != null ||
    technicals.pivotClassicR1 != null;

  if (!hasContent) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Market technicals (Tradient)
      </Typography>
      <Grid container spacing={1.5}>
        {technicals.recommendAllLabel && (
          <Grid item xs={12} sm={6} md={4}>
            <MetricCard
              label="Overall signal"
              value={technicals.recommendAllLabel}
              hint={
                technicals.recommendAll != null
                  ? `Score ${technicals.recommendAll.toFixed(2)} (−1 sell … +1 buy)`
                  : undefined
              }
              chip={{ label: technicals.recommendAllLabel, color: signalColor(technicals.recommendAllLabel) }}
            />
          </Grid>
        )}
        {technicals.rsi != null && (
          <Grid item xs={12} sm={6} md={4}>
            <MetricCard
              label="RSI (14)"
              value={technicals.rsi.toFixed(1)}
              chip={
                technicals.rsiSignal
                  ? { label: technicals.rsiSignal, color: signalColor(technicals.rsiSignal) }
                  : undefined
              }
            />
          </Grid>
        )}
        {technicals.adx != null && (
          <Grid item xs={12} sm={6} md={4}>
            <MetricCard
              label="ADX (trend)"
              value={technicals.adx.toFixed(1)}
              chip={
                technicals.trendStrength
                  ? { label: technicals.trendStrength, color: 'default' }
                  : undefined
              }
            />
          </Grid>
        )}
        {technicals.macdSignalLabel && (
          <Grid item xs={12} sm={6} md={4}>
            <MetricCard
              label="MACD"
              value={technicals.macdSignalLabel}
              hint={
                technicals.macd != null && technicals.macdSignal != null
                  ? `MACD ${technicals.macd.toFixed(4)} · Signal ${technicals.macdSignal.toFixed(4)}`
                  : undefined
              }
              chip={{ label: technicals.macdSignalLabel, color: signalColor(technicals.macdSignalLabel) }}
            />
          </Grid>
        )}
        {technicals.stochSignal && (
          <Grid item xs={12} sm={6} md={4}>
            <MetricCard
              label="Stochastic"
              value={
                technicals.stochK != null && technicals.stochD != null
                  ? `K ${technicals.stochK.toFixed(1)} · D ${technicals.stochD.toFixed(1)}`
                  : technicals.stochSignal
              }
              chip={{ label: technicals.stochSignal, color: signalColor(technicals.stochSignal) }}
            />
          </Grid>
        )}
        {(technicals.recommendMaLabel || technicals.recommendOtherLabel) && (
          <Grid item xs={12} sm={6} md={4}>
            <MetricCard
              label="MA / Other signals"
              value={[technicals.recommendMaLabel, technicals.recommendOtherLabel].filter(Boolean).join(' · ')}
            />
          </Grid>
        )}
        {technicals.sma20 != null && (
          <Grid item xs={12} sm={6} md={4}>
            <MetricCard
              label="SMA 20"
              value={formatCurrency(technicals.sma20)}
              chip={
                technicals.priceVsSma20
                  ? { label: technicals.priceVsSma20, color: signalColor(technicals.priceVsSma20) }
                  : undefined
              }
            />
          </Grid>
        )}
        {technicals.sma50 != null && (
          <Grid item xs={12} sm={6} md={4}>
            <MetricCard
              label="SMA 50"
              value={formatCurrency(technicals.sma50)}
              chip={
                technicals.priceVsSma50
                  ? { label: technicals.priceVsSma50, color: signalColor(technicals.priceVsSma50) }
                  : undefined
              }
            />
          </Grid>
        )}
        {technicals.sma200 != null && (
          <Grid item xs={12} sm={6} md={4}>
            <MetricCard label="SMA 200" value={formatCurrency(technicals.sma200)} />
          </Grid>
        )}
        {technicals.pivotClassicR1 != null && (
          <Grid item xs={12} sm={6} md={4}>
            <MetricCard
              label="Classic pivots"
              value={`R1 ${formatCurrency(technicals.pivotClassicR1)}`}
              hint={
                technicals.pivotClassicMiddle != null && technicals.pivotClassicS1 != null
                  ? `Pivot ${formatCurrency(technicals.pivotClassicMiddle)} · S1 ${formatCurrency(technicals.pivotClassicS1)}`
                  : undefined
              }
            />
          </Grid>
        )}
      </Grid>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Technical indicators from Tradient daily scan — not company fundamentals (P/E, revenue, etc.).
        {lastClosePrice != null ? ` Price uses close|1 = ${formatCurrency(lastClosePrice)}.` : ''}
      </Typography>
    </Box>
  );
};

export default MarketTechnicalsSection;
